import React, { createContext, Dispatch, SetStateAction, useState, useContext, useCallback, useMemo } from 'react';
import { AlertDialog } from '../components/AlertDialog';

interface IProps {
  children: JSX.Element;
}

interface IDialogConfig {
  title: string;
  text: string;
  open?: boolean;
  onProceed?: () => void;
}

const initialState: IDialogConfig = {
  title: '',
  text: '',
  open: false,
  onProceed: () => undefined,
};

export const AlertDialogContextComponent = (props: IProps): JSX.Element => {
  const [dialogConfig, setDialogConfig] = useState<IDialogConfig>(initialState);

  const { title, text, open, onProceed } = dialogConfig;

  const close = () => {
    const cloned = { ...dialogConfig };
    cloned.open = false;
    setDialogConfig(cloned);
  };

  return (
    <AlertDialogContext.Provider value={setDialogConfig}>
      {props.children}
      <AlertDialog
        open={!!open}
        onPressButton={() => {
          close();
          if (onProceed) {
            onProceed();
          }
        }}
        text={text}
        title={title}
      />
    </AlertDialogContext.Provider>
  );
};

const AlertDialogContext = createContext<Dispatch<SetStateAction<IDialogConfig>>>(() => undefined);

const useAlertDialog = () => {
  const alertDialogContext = useContext(AlertDialogContext);

  const open = useCallback(
    (config: IDialogConfig) => {
      alertDialogContext({
        open: true,
        ...config,
      });
    },
    [alertDialogContext],
  );

  const close = useCallback(() => {
    alertDialogContext({
      open: false,
      title: '',
      text: '',
      onProceed: () => undefined,
    });
  }, [alertDialogContext]);

  return useMemo(() => ({ open, close }), [open, close]);
};

export default useAlertDialog;
